import { SpanStatusCode } from '@opentelemetry/api';
import { delay } from 'src/common/helpers/delay-helper';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { IDeviceConfigurationDictionary } from 'src/device-status/interfaces/IDeviceConfigurationDictionary';
import { updateDeviceStatus } from '../common/device/initialize-devices';
import { DeviceType } from '../common/enumerations/device-type';
import { EmeraldDialogTypes } from '../common/enumerations/emerald-dialog-types';
import XLRLogger from '../common/helpers/logger';
import { getValueAsNumber } from '../common/helpers/number-helper';
import { IDeviceStatusItem } from '../common/interfaces/states/IDeviceStatusItem';
import { store } from '../common/redux/configure-store';
import { CashDrawerActions } from '../common/redux/reducers/cash-drawer-reducer';
import { popupMessage } from '../messages/business-logic/popup-message';
import { RdsResponseType } from './device-enums';
import { JposConst } from './jpos-const';
import { DeviceResponse, sendPeripheralMessage } from './rds-connection';

const CHECK_ATTEMPTS = 20;
const CHECK_INTERVAL = 200;
const loggerSource = 'devices/cash-drawer-operations.ts';

export const hasCashDrawer = (): boolean => {
  const cashDrawer = getCashDrawer();
  return !!cashDrawer?.initialized;
};

const getCashDrawer: () => IDeviceStatusItem | undefined = () =>
  store.getState().deviceStatus.devices.find(i => i.deviceType === DeviceType.CashDrawer);

const showFailurePrompt: () => Promise<boolean> = () => {
  return new Promise<boolean>(resolve => {
    const message: IMessageModal = {
      message: 'CriticalErrorCashDrawer',
      type: EmeraldDialogTypes.Error,
      subMessage: 'ReinitializingPeripherals',
      onAccept: () => {
        resolve(false);
      },
    };
    popupMessage(message);
  });
};

const showRetryPrompt: () => Promise<boolean> = () => {
  return new Promise<boolean>(resolve => {
    const message: IMessageModal = {
      message: 'CashDrawerOpenAction_Error',
      type: EmeraldDialogTypes.Error,
      subMessage: 'IsLocked',
      onAccept: () => {
        resolve(true);
      },
      onCancel: () => {
        resolve(false);
      },
      acceptButtonLabel: 'TryAgain',
    };
    popupMessage(message);
  });
};

export const openCashDrawer = async (): Promise<boolean> => {
  const openDrawerSpan = createSpan('[RDS] open-cash-drawer', undefined, true);

  let retry = true;

  do {
    if (!hasCashDrawer()) {
      openDrawerSpan.setStatus({
        code: SpanStatusCode.ERROR,
        message: 'openCashDrawer error - no cash drawer',
      });
      return false;
    }

    await openDrawer().then(response => {
      if (response === null) {
        return;
      }
      if (response.responseType == RdsResponseType.ERROR) {
        openDrawerSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: 'openCashDrawer error - the drawer open request failed',
        });
      }
    });

    let triesLeft = CHECK_ATTEMPTS;
    while (triesLeft--) {
      if (store.getState().cashDrawer.open) {
        endSpan(openDrawerSpan);
        return true;
      }

      await delay(CHECK_INTERVAL);
    }

    const userActionSpan = createSpan(
      'user-action-open-cash-drawer-retry',
      openDrawerSpan.spanContext(),
      true,
    );
    retry = retry ? await showRetryPrompt() : await showFailurePrompt();
    endSpan(userActionSpan);
  } while (retry);

  openDrawerSpan.setStatus({
    code: SpanStatusCode.ERROR,
    message: 'openCashDrawer error - timeout',
  });
  endSpan(openDrawerSpan);
  return false;
};

const onCashDrawerChange = (drawerOpen: boolean): void => {
  store.dispatch(CashDrawerActions.setCashDrawerState(drawerOpen));
};

const openDrawer = (): Promise<DeviceResponse> => {
  const logicalName =
    store.getState().deviceStatus.devices.find(d => d.deviceType == DeviceType.CashDrawer)
      ?.logicalName || '';
  return sendPeripheralMessage({ type: DeviceType.CashDrawer, logicalName }, '/cash-drawer/open');
};

const getCashDrawerConfig = (): IDeviceConfigurationDictionary | undefined => {
  return store
    .getState()
    .touchPoint.touchPointConfiguration.deviceList?.find(i => i.Type === DeviceType.CashDrawer);
};

export const handleCashDrawerMessage = (cashDrawerData: DeviceResponse): void => {
  if (cashDrawerData.error) {
    XLRLogger.error({
      message: 'Handle Cash Drawer Message Error',
      source: loggerSource,
      data: cashDrawerData,
    });
    return;
  }

  const deviceName = cashDrawerData.metaDataMap.logicalName;
  let cashDrawerStatus = cashDrawerData.metaDataMap.status;
  switch (cashDrawerData.responseType) {
    case RdsResponseType.STATUS_UPDATE:
      if (getCashDrawerConfig()?.InvertOpenCloseEventLogic === 'true') {
        if (cashDrawerStatus == '0') {
          cashDrawerStatus = '1';
          cashDrawerData.metaDataMap.statusMessage = JposConst.JPOS_CASH_SUE_DRAWEROPEN;
        } else {
          cashDrawerStatus = '0';
          cashDrawerData.metaDataMap.statusMessage = JposConst.JPOS_CASH_SUE_DRAWERCLOSED;
        }
      }
      XLRLogger.info({
        message: `OnCashDrawerChange Status: ${cashDrawerStatus}`,
        source: loggerSource,
        data: cashDrawerData,
      });
      onCashDrawerChange(!!getValueAsNumber(cashDrawerStatus));
      break;
    case RdsResponseType.HEALTH_CHECK_SUCCESS:
      if (deviceName) {
        updateDeviceStatus({
          deviceName,
          initialized: true,
          deviceType: DeviceType.CashDrawer,
        });
      }
      XLRLogger.debug({
        message: `CashDrawer Status: ${cashDrawerData.message}`,
        source: loggerSource,
        data: cashDrawerData,
      });
      break;
    case RdsResponseType.HEALTH_CHECK_FAILURE:
      if (deviceName) {
        updateDeviceStatus({
          deviceName,
          initialized: false,
          deviceType: DeviceType.CashDrawer,
        });
      }
      XLRLogger.debug({
        message: `CashDrawer Status: ${cashDrawerData.message}`,
        source: loggerSource,
        data: cashDrawerData,
      });
      break;
    default:
      XLRLogger.info({
        message: 'Unrecognized message from Cash Drawer',
        source: loggerSource,
        data: cashDrawerData,
      });
      break;
  }
};
